import debounce from 'lodash/debounce';
import React, { useCallback, useState } from 'react';
import LoadingIndicator from 'src/components/LoadingIndicator';
import PaginationBtns from 'src/components/PaginationBtns';
import emptyImg from 'src/images/empty.svg';
import sanFranImg from 'src/images/san-francisco.svg';
import {
  Badge,
  Button,
  Card,
  Col,
  Container,
  Icon,
  Link,
  ListGroup,
  ListGroupItem,
  Row,
  ScreenSize,
  Table,
  TableBody,
  TableData,
  TableHead,
  TableHeader,
  TableRow,
  TableSearch,
  useApp,
  useUrlState
} from 'straightline-ui';

export default function AddressDetails() {
  const app = useApp();
  const { getInState, setInState, delInState } = useUrlState();
  const query = getInState('query', {
    $sort: { default_address: 1, 'address.name': 1 }
  });
  const $sort = getInState('query.$sort', {
    default_address: 1,
    'address.name': 1
  });
  const $search = getInState('query.$search', '');
  const [text, setText] = useState($search);

  const {
    result: addressDetails,
    isLoading,
    isError,
    error,
    isFetching
  } = app
    .service('api/address-details')
    .useFind(
      { query: { ...query, $sort }, totals: true },
      { keepPreviousData: true }
    );

  // eslint-disable-next-line
  const debouncedHandleChange = useCallback(
    debounce((value) => {
      const val = value && value.trim ? value.trim() : value;
      if (val) {
        setInState('query.$search', val);
      } else {
        delInState('query.$search');
      }
    }, 300),
    []
  );

  const handleSearchChange = ({ target: { value } }) => {
    setText(value);
    debouncedHandleChange(value);
  };

  const handleSearchClear = async () => {
    setText('');
    delInState('query.$search');
  };

  const handleSort = ({ name, value }) => {
    setInState('query.$sort', { [name]: value });
  };

  if (isLoading) {
    return (
      <div className="d-flex vh-100 align-items-center">
        <LoadingIndicator delay={250} />
      </div>
    );
  }

  if (isError) {
    return (
      <Container>
        <Row className="align-items-center justify-content-center vh-100">
          <Col lg="6" className="text-center">
            <img
              src={emptyImg}
              alt="Not Found"
              className="img-fluid animated fadeIn faster mb-5"
            />
            <h1>{error.message || 'There was an unexpected error'}</h1>
            <Button to="/dashboard" color="primary" size="lg" className="mr-3">
              <Icon type="home" className="mr-2" />
              Return to Dashboard
            </Button>
            <Button to="/support" color="secondary" size="lg">
              <Icon type="send" className="mr-2" />
              Contact Straightline
            </Button>
          </Col>
        </Row>
      </Container>
    );
  }

  if (addressDetails.total === 0 && !query.$search) {
    return (
      <Container>
        <Row className="align-items-center justify-content-center vh-100">
          <Col lg="7" className="text-center">
            <img
              src={sanFranImg}
              alt="Create new address"
              className="img-fluid animated fadeIn faster mb-5"
            />
            <h1>Address Book</h1>
            <p>
              Save addresses with names, descriptions, and other default
              details. You can then access these addresses when requesting new
              quotes or searching shipments.
            </p>
            <Button to="/address" className="px-5" color="primary">
              <Icon type="map" className="mr-2" />
              New Address
            </Button>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container fluid className="pb-5">
      <div className="header pt-md-2">
        <div className="header-body">
          <Row className="align-items-center">
            <Col>
              <h6 className="header-pretitle">Overview</h6>
              <h1 className="header-title">Addresses</h1>
            </Col>
            <Col className="col-auto">
              <Button to="/address" color="primary">
                <Icon type="map" className="mr-2" />
                New Address
              </Button>
            </Col>
          </Row>
        </div>
      </div>
      <div className={isFetching ? 'is-fetching' : ''}>
        <ScreenSize md lg xl>
          {() => (
            <AddressesTable
              data={addressDetails.data}
              text={text}
              sort={handleSort}
              $sort={$sort}
              handleSearchChange={handleSearchChange}
              handleSearchClear={handleSearchClear}
            />
          )}
        </ScreenSize>
        <ScreenSize xs sm>
          {() => <AddressesList data={addressDetails.data} />}
        </ScreenSize>
      </div>
      <div className="text-right">
        <PaginationBtns
          total={addressDetails.total}
          skip={getInState('query.$skip', 0)}
          setSkip={($skip) => setInState('query.$skip', $skip)}
        />
      </div>
    </Container>
  );
}

const AddressesTable = ({
  data,
  text,
  sort,
  $sort,
  handleSearchChange,
  handleSearchClear
}) => {
  return (
    <Card>
      <TableSearch
        value={text}
        onChange={handleSearchChange}
        onClear={handleSearchClear}
        disabled={!text}
        placeholder="Search by name, cites, states"
      />
      <div className="table-responsive">
        <Table className="table table-nowrap card-table table-hover">
          <TableHead>
            <TableRow>
              <TableHeader
                className="py-3"
                sortName="address.name"
                sort={$sort['address.name']}
                onClick={sort}
              >
                Name
              </TableHeader>
              <TableHeader className="py-3">Address</TableHeader>
              <TableHeader className="py-3">Quotes</TableHeader>
              <TableHeader className="py-3">Shipments</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody className="list">
            {data.map((addressDetail) => (
              <TableRow
                key={addressDetail.id}
                to={`/addresses/${addressDetail.id}`}
              >
                <TableData className="text-truncate" style={{ maxWidth: 300 }}>
                  <div className="mb-0 font-weight-bold">
                    {addressDetail.address.name}
                    {addressDetail.default_address && (
                      <span className="ml-2 text-primary small">
                        Default{' '}
                        {addressDetail.default_address.charAt(0).toUpperCase() +
                          addressDetail.default_address.slice(1)}
                      </span>
                    )}
                  </div>
                  <div className="text-truncate text-secondary">
                    {addressDetail.description || 'Description not available'}
                  </div>
                </TableData>

                <TableData>
                  <div>{addressDetail.address.address_1}</div>
                  <div>
                    {addressDetail.address.city}, {addressDetail.address.state},{' '}
                    {addressDetail.address.postal_code}
                  </div>
                </TableData>
                <TableData>
                  <div>
                    Pickups:{' '}
                    <Badge color="soft-primary">
                      {addressDetail.totals.shipmentQuotes.pickups}
                    </Badge>
                  </div>
                  <div>
                    Deliveries:{' '}
                    <Badge color="soft-primary">
                      {addressDetail.totals.shipmentQuotes.deliveries}
                    </Badge>
                  </div>
                </TableData>
                <TableData>
                  <div>
                    Pickups:{' '}
                    <Badge color="soft-primary">
                      {addressDetail.totals.shipments.pickups}
                    </Badge>
                  </div>
                  <div>
                    Deliveries:{' '}
                    <Badge color="soft-primary">
                      {addressDetail.totals.shipments.deliveries}
                    </Badge>
                  </div>
                </TableData>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
};

const AddressesList = ({ data }) => {
  return (
    <div>
      <Card className="mx-n3">
        <ListGroup className="list-group-flush">
          {data.map((addressDetail) => {
            return (
              <ListGroupItem
                key={addressDetail.id}
                tag={Link}
                to={`/addresses/${addressDetail.id}`}
                className="list-group-item-action px-3"
              >
                <ListGroup className="list-group list-group-flush list-group-activity">
                  <ListGroupItem className="py-3">
                    <Row>
                      <Col xs="auto">
                        <div className="avatar avatar-sm">
                          <div className="avatar-title font-size-lg rounded-circle bg-light text-dark">
                            <Icon type="map" />
                          </div>
                        </div>
                      </Col>
                      <Col className="ml-n2">
                        <div className="font-weight-bold lead">
                          {addressDetail.address.name}
                        </div>
                        <div className="text-secondary">
                          {addressDetail.description ||
                            'Description not available'}
                        </div>
                        <div>{addressDetail.address.address_1}</div>
                        <div>
                          {addressDetail.address.city},{' '}
                          {addressDetail.address.state},{' '}
                          {addressDetail.address.postal_code}
                        </div>
                      </Col>
                    </Row>
                  </ListGroupItem>
                  <ListGroupItem className="py-3">
                    <Row>
                      <Col xs="auto">
                        <div className="avatar avatar-sm">
                          <div className="avatar-title font-size-lg rounded-circle bg-light text-dark">
                            <Icon type="truck" />
                          </div>
                        </div>
                      </Col>
                      <Col className="ml-n2">
                        <div className="font-weight-bold">Pickups</div>
                        <div>
                          Quotes: {addressDetail.totals.shipmentQuotes.pickups}
                        </div>
                        <div>
                          Shipments: {addressDetail.totals.shipments.pickups}
                        </div>
                      </Col>
                    </Row>
                  </ListGroupItem>
                  <ListGroupItem className="py-3">
                    <Row>
                      <Col xs="auto">
                        <div className="avatar avatar-sm">
                          <div className="avatar-title font-size-lg rounded-circle bg-light text-dark">
                            <Icon type="map-pin" />
                          </div>
                        </div>
                      </Col>
                      <Col className="ml-n2">
                        <div className="font-weight-bold">Deliveries</div>
                        <div>
                          Quotes:{' '}
                          {addressDetail.totals.shipmentQuotes.deliveries}
                        </div>
                        <div>
                          Shipments: {addressDetail.totals.shipments.deliveries}
                        </div>
                      </Col>
                    </Row>
                  </ListGroupItem>
                </ListGroup>
              </ListGroupItem>
            );
          })}
        </ListGroup>
      </Card>
    </div>
  );
};
